import { supabase } from '../src/lib/supabase';
import { Project, UserProfile, SiteConfig, InboxMessage } from '../types';
import { getProjectContentOverride } from '../constants/projectContentOverrides';
import { resolveProjectSlug } from '../utils/projectSlug';

// --- PROJECTS ---

export const getProjects = async (): Promise<Project[]> => {
  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .order('sort_order', { ascending: true });

  if (error) {
    console.error('Error fetching projects:', error);
    return [];
  }
  
  // Преобразуем данные из базы (snake_case) в наш код (camelCase)
  return data.map((p: any) => {
    const title = p.title || p.name;
    const slug = resolveProjectSlug({ id: p.id, title, name: p.name, slug: p.slug });
    const contentOverride = getProjectContentOverride(p.id, title);

    return {
      ...p,
      slug,
      title, // Поддержка обоих полей
      logoUrl: p.logo_url,
      websiteUrl: contentOverride?.websiteUrl || p.website_url,
      crunchbase: p.crunchbase ?? null,
      crunchbaseText: p.crunchbase_text ?? null,
      defaultShowcaseView: p.default_showcase_view ?? null,
      audioUrl: p.audio_url,
      content: contentOverride?.content || p.content,
      description: contentOverride?.description || p.description,
      desktopShowcaseImages: Array.isArray(p.desktop_showcase_images) ? p.desktop_showcase_images : null,
      mobileShowcaseImages: Array.isArray(p.mobile_showcase_images) ? p.mobile_showcase_images : null,
      // fallback to legacy "order" if present
      sortOrder: p.sort_order ?? p.order,
      // JSON поля приходят как объекты, это нормально
    };
  }) || [];
};

const isSlugColumnMissing = (error: { code?: string; message?: string } | null) => {
  const message = typeof error?.message === 'string' ? error.message : '';
  const code = error?.code;
  const slugColumnPhrase = message.includes("'slug' column") || message.includes('column "slug"');
  return code === 'PGRST204' ||
    code === '42703' ||
    (slugColumnPhrase && (
      message.includes('schema cache') ||
      message.includes('does not exist') ||
      message.includes('column "slug"')
    ));
};

export const saveProjects = async (projects: Project[]): Promise<void> => {
  // Мы сохраняем проекты по одному или массивом, но нужно мапить поля
  const dbProjects = projects.map(p => ({
    id: p.id,
    slug: resolveProjectSlug(p),
    name: p.title, // В базу пишем title в колонку name (или title если добавили)
    title: p.title,
    description: p.description,
    content: p.content, // ДОБАВЛЕНО: Сохранение поля content (Deep Dive)
    stage: p.stage,
    role: p.role,
    logo_url: p.logoUrl,
    website_url: p.websiteUrl,
    crunchbase: p.crunchbase?.trim() ? p.crunchbase.trim() : null,
    crunchbase_text: p.crunchbaseText?.trim() ? p.crunchbaseText.trim() : null,
    audio_url: p.audioUrl,
    desktop_showcase_images: p.desktopShowcaseImages && p.desktopShowcaseImages.length > 0 ? p.desktopShowcaseImages : null,
    mobile_showcase_images: p.mobileShowcaseImages && p.mobileShowcaseImages.length > 0 ? p.mobileShowcaseImages : null,
    default_showcase_view: p.defaultShowcaseView ?? null,
    tags: p.tags,
    metrics: p.metrics, // JSONB
    socials: p.socials,  // JSONB
    sort_order: p.sortOrder
  }));

  const { error } = await supabase
    .from('projects')
    .upsert(dbProjects);

  if (error) {
    if (isSlugColumnMissing(error)) {
      const projectsForRetry = dbProjects.map(({ slug, ...rest }) => rest);
      const { error: fallbackError } = await supabase
        .from('projects')
        .upsert(projectsForRetry);

      if (!fallbackError) return;

      console.error('Error saving projects:', fallbackError);
      throw fallbackError;
    }

    console.error('Error saving projects:', error);
    throw error;
  }
};

export const deleteProject = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('projects')
    .delete()
    .eq('id', id);

  if (error) throw error;
};

// --- PROFILE ---

export const getProfile = async (): Promise<UserProfile | null> => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .limit(1)
    .single();

  if (error) return null;

  // Маппинг из базы в код
  return {
    ...data,
    avatarUrl: data.avatar_url,
    bookingUrl: data.booking_url,
    investmentCard: data.investment_card,
    // experience и education уже придут как JSON массивы
  };
};

export const saveProfile = async (profile: UserProfile): Promise<void> => {
  const { data: existingData } = await supabase
    .from('profiles')
    .select('id')
    .limit(1)
    .single();

  // Маппинг из кода в базу
  const dbProfile = {
    name: profile.name,
    nickname: profile.nickname,
    email: profile.email,
    location: profile.location,
    bio: profile.bio,
    avatar_url: profile.avatarUrl,
    booking_url: profile.bookingUrl,
    socials: profile.socials,
    skills: profile.skills,
    experience: profile.experience,
    education: profile.education,
    investment_card: profile.investmentCard
  };

  if (existingData && existingData.id) {
    const { error } = await supabase
      .from('profiles')
      .update(dbProfile)
      .eq('id', existingData.id);
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('profiles')
      .insert(dbProfile);
    if (error) throw error;
  }
};

// --- SITE CONFIG ---

export const getSiteConfig = async (): Promise<SiteConfig | null> => {
  const { data, error } = await supabase
    .from('configs')
    .select('*')
    .limit(1)
    .single();

  if (error) return null;

  return {
    maintenanceMode: data.maintenance_mode,
    welcomeMessage: data.welcome_message,
    footerText: data.footer_text,
    heroHeadline: data.hero_headline,
    heroSubheadline: data.hero_subheadline,
    heroCtaText: data.hero_cta_text,
    metaTitle: data.meta_title
  };
};

export const saveSiteConfig = async (config: SiteConfig): Promise<void> => {
  const { data: existingData } = await supabase
    .from('configs')
    .select('id')
    .limit(1)
    .single();

  const dbConfig = {
    hero_headline: config.heroHeadline,
    hero_subheadline: config.heroSubheadline,
    hero_cta_text: config.heroCtaText,
    meta_title: config.metaTitle,
    // добавь остальные если нужно
  };

  if (existingData && existingData.id) {
    const { error } = await supabase.from('configs').update(dbConfig).eq('id', existingData.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('configs').insert(dbConfig);
    if (error) throw error;
  }
};

// --- INBOX ---

export const getInboxMessages = async (): Promise<InboxMessage[]> => {
  const { data, error } = await supabase
    .from('inbox_messages')
    .select('*')
    .order('date', { ascending: false });

  if (error) return [];
  
  return data.map((m: any) => ({
    ...m,
    projectInterest: m.project_interest
  }));
};

export const saveInboxMessage = async (message: InboxMessage): Promise<void> => {
  const { error } = await supabase
    .from('inbox_messages')
    .insert({
      ...message,
      project_interest: message.projectInterest
    });

  if (error) throw error;
};

export const markMessageAsRead = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('inbox_messages')
    .update({ read: true })
    .eq('id', id);

  if (error) throw error;
};
